// ============================================================
// calendar/calendarData.ts — the REAL Quam Calendar data layer (PHASE_2M D3).
//
// Replaces seedMonth + localStorage entirely. Every item on the month grid is
// a live backend record:
//   google_event  — Google Calendar via the live quam-calendar runtime
//   draft         — sq_quam_calendar_event_drafts (draft → approve → commit)
//   plan          — sq_calendar_plans (Ask-Quam planning requests)
//   call          — Daily.co controlled meetings (/api/square/daily/meetings)
//   daily_meeting — sq_daily_meetings (the internal record, Operations)
//   task          — sq_team_tasks with a due date
// plus per-anchor workspace ITEMS (notes / links / file refs / messages) from
// sq_calendar_workspace_items (source-ready migration 20260711000001 — until
// the operator applies it the API returns the exact MIGRATION_APPROVAL_REQUIRED
// state and the panels say so; nothing is faked).
//
// File attachments use the REAL files spine (signed upload/download URLs on
// the private 'files' bucket); a missing bucket surfaces as
// STORAGE_PROVIDER_REQUIRED with the server's message.
// ============================================================

import { commandCenterPath } from '../commandCenterRoutes'
import { useCallback, useEffect, useState } from 'react'
import { api, apiRequest, ApiError, isAuthError, type Meeting, type CalendarWorkspaceItem } from '../../../api/client'
import { listDailyMeetings, type DailyMeetingRecord } from '../operations/daily-meetings/dailyMeetings'
import { listTeamTasks, mutateTask, type TeamTask } from '../comms/commsData'

export const CALENDAR_ROOT = commandCenterPath('calendar')

// ---- item + day model -------------------------------------------------------

export type CalKind = 'google_event' | 'draft' | 'plan' | 'call' | 'daily_meeting' | 'task'

export const CAL_KIND_META: Record<CalKind, { label: string; color: string }> = {
  google_event: { label: 'Event', color: '#5B9FD4' },
  draft: { label: 'Draft', color: '#9b7ec7' },
  plan: { label: 'Plan', color: '#00a67d' },
  call: { label: 'Call', color: '#e85d3a' },
  daily_meeting: { label: 'Meeting', color: '#E8854A' },
  task: { label: 'Task', color: '#c4a24e' },
}

export interface CalItem {
  key: string
  kind: CalKind
  id: string
  title: string
  timeLabel: string
  dateKey: string // YYYY-MM-DD
  status?: string
  detail?: string
  /** For workspace items: the anchor this record answers to. */
  anchorKind: string
  raw: Record<string, unknown>
}

export interface CalDay {
  date: number
  dateKey: string
  dow: number
  letter: string
  items: CalItem[]
}

export interface GoogleState {
  connected: boolean
  reason: string | null
  email: string | null
}

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

export function monthDateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function toDateKey(iso: string | null | undefined): string | null {
  if (!iso) return null
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso)
  return m ? m[1] : null
}

export function timeLabel(iso: string | null | undefined): string {
  if (!iso) return ''
  const t = new Date(iso)
  if (!Number.isFinite(t.getTime())) return ''
  if (!iso.includes('T')) return 'All day'
  return t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

// ---- real mappers (one per backend source) ----------------------------------

export function googleEventToItem(e: Record<string, any>): CalItem | null {
  const startIso: string | undefined = e?.start?.dateTime || e?.start?.date
  const dateKey = toDateKey(startIso)
  if (!dateKey || !e.id) return null
  return {
    key: `g-${e.id}`, kind: 'google_event', id: String(e.id),
    title: String(e.summary || '(untitled event)'), timeLabel: timeLabel(startIso),
    dateKey, status: String(e.status || ''), detail: typeof e.description === 'string' ? e.description : undefined,
    anchorKind: 'google_event', raw: e,
  }
}

export function draftToItem(d: Record<string, any>): CalItem | null {
  const dateKey = toDateKey(d.start_at)
  if (!dateKey || !d.id) return null
  return {
    key: `d-${d.id}`, kind: 'draft', id: String(d.id),
    title: String(d.title || '(untitled draft)'), timeLabel: timeLabel(d.start_at),
    dateKey, status: String(d.status || 'draft'), detail: typeof d.description === 'string' ? d.description : undefined,
    anchorKind: 'draft', raw: d,
  }
}

export function planToItem(p: Record<string, any>): CalItem | null {
  const dateKey = toDateKey(p.created_at)
  if (!dateKey || !p.id) return null
  return {
    key: `p-${p.id}`, kind: 'plan', id: String(p.id),
    title: String(p.request_text || p.summary || 'Quam plan'), timeLabel: timeLabel(p.created_at),
    dateKey, status: String(p.status || ''), anchorKind: 'plan', raw: p,
  }
}

export function callToCalItem(m: Meeting): CalItem | null {
  const iso = m.starts_at || m.created_at
  const dateKey = toDateKey(iso)
  if (!dateKey) return null
  return {
    key: `c-${m.id}`, kind: 'call', id: m.id, title: m.title, timeLabel: timeLabel(iso),
    dateKey, status: m.status, detail: `room ${m.daily_room_name}`, anchorKind: 'meeting', raw: m as unknown as Record<string, unknown>,
  }
}

export function dailyMeetingToCalItem(m: DailyMeetingRecord): CalItem | null {
  if (!m.meeting_date) return null
  return {
    key: `dm-${m.id}`, kind: 'daily_meeting', id: m.id, title: m.meeting_title,
    timeLabel: m.meeting_type, dateKey: m.meeting_date, status: m.follow_up_needed ? 'follow-up' : undefined,
    anchorKind: 'daily_meeting', raw: m as unknown as Record<string, unknown>,
  }
}

export function taskToCalItem(t: TeamTask): CalItem | null {
  const dateKey = toDateKey(t.due_at)
  if (!dateKey) return null
  return {
    key: `t-${t.id}`, kind: 'task', id: t.id, title: t.title, timeLabel: timeLabel(t.due_at),
    dateKey, status: t.status, detail: t.blocker_reason || undefined, anchorKind: 'task', raw: t as unknown as Record<string, unknown>,
  }
}

export function buildMonthDays(year: number, month: number, items: CalItem[]): CalDay[] {
  const count = new Date(year, month + 1, 0).getDate()
  const byDate = new Map<string, CalItem[]>()
  for (const it of items) {
    const list = byDate.get(it.dateKey)
    if (list) list.push(it)
    else byDate.set(it.dateKey, [it])
  }
  return Array.from({ length: count }, (_, i) => {
    const date = i + 1
    const dateKey = monthDateKey(year, month, date)
    const dow = new Date(year, month, date).getDay()
    return { date, dateKey, dow, letter: DOW[dow], items: byDate.get(dateKey) || [] }
  })
}

// ---- error humanizers (exact states, never broad) ---------------------------

export function humanizeCalendarError(err: unknown): string {
  if (err instanceof ApiError) return err.message || err.code
  return 'Could not reach the calendar backend.'
}

/** The workspace-items table ships in 20260711000001 (source-ready). */
export const ITEMS_MIGRATION_STATE = 'MIGRATION_APPROVAL_REQUIRED'
export function isItemsMigrationError(err: unknown): boolean {
  return err instanceof ApiError && (err.code === 'backend_not_migrated' || /sq_calendar_workspace_items|20260711000001/i.test(err.message || ''))
}

// ---- the workspace hook ------------------------------------------------------

export interface QuamCalendarState {
  loading: boolean
  error: string | null
  authRequired: boolean
  google: GoogleState
  days: CalDay[]
  /** Exact state of the notes/links/files/messages spine. */
  itemsState: 'live' | 'migration_required' | 'error' | 'loading'
  itemsError: string | null
  items: CalendarWorkspaceItem[]
}

export function useQuamCalendar(year: number, month: number) {
  const [state, setState] = useState<QuamCalendarState>({
    loading: true, error: null, authRequired: false,
    google: { connected: false, reason: null, email: null },
    days: buildMonthDays(year, month, []),
    itemsState: 'loading', itemsError: null, items: [],
  })

  const load = useCallback(async (signal?: AbortSignal) => {
    setState((s) => ({ ...s, loading: true, error: null }))
    const monthStart = new Date(Date.UTC(year, month, 1)).toISOString()
    const monthEnd = new Date(Date.UTC(year, month + 1, 1)).toISOString()
    const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`

    // Six independent real loads — each failure stays scoped + honest.
    const [events, calls, dailies, tasks, status, items] = await Promise.allSettled([
      api.quamCalendar.events({ time_min: monthStart, time_max: monthEnd }),
      api.commandCenter.meetings.list(),
      listDailyMeetings(signal),
      listTeamTasks(signal),
      api.quamCalendar.status(),
      api.quamCalendar.workspaceItems.list({ month: monthKey }),
    ])
    if (signal?.aborted) return

    const anyAuth = [events, calls, dailies, tasks, status].some((r) => r.status === 'rejected' && isAuthError(r.reason))
    if (anyAuth) {
      setState((s) => ({ ...s, loading: false, authRequired: true }))
      return
    }

    const all: CalItem[] = []
    let google: GoogleState = { connected: false, reason: null, email: null }
    let error: string | null = null

    if (events.status === 'fulfilled') {
      const gs = (events.value.google_status || {}) as Record<string, unknown>
      google = { connected: gs.connected === true, reason: typeof gs.reason === 'string' ? gs.reason : (typeof gs.error === 'string' ? gs.error : null), email: null }
      for (const e of events.value.google_events || []) { const it = googleEventToItem(e); if (it) all.push(it) }
      for (const d of events.value.drafts || []) { const it = draftToItem(d); if (it) all.push(it) }
      for (const p of events.value.quam_plans || []) { const it = planToItem(p); if (it && it.dateKey.startsWith(monthKey)) all.push(it) }
    } else {
      error = humanizeCalendarError(events.reason)
    }
    if (status.status === 'fulfilled') {
      google = { connected: status.value.connected, reason: status.value.reason, email: status.value.google_email }
    }
    if (calls.status === 'fulfilled') {
      for (const m of calls.value.meetings || []) { const it = callToCalItem(m); if (it && it.dateKey.startsWith(monthKey)) all.push(it) }
    }
    if (dailies.status === 'fulfilled') {
      for (const m of dailies.value) { const it = dailyMeetingToCalItem(m); if (it && it.dateKey.startsWith(monthKey)) all.push(it) }
    }
    if (tasks.status === 'fulfilled') {
      for (const t of tasks.value) { const it = taskToCalItem(t); if (it && it.dateKey.startsWith(monthKey)) all.push(it) }
    }

    let itemsState: QuamCalendarState['itemsState'] = 'live'
    let itemsError: string | null = null
    let itemRows: CalendarWorkspaceItem[] = []
    if (items.status === 'fulfilled') {
      itemRows = items.value.items || []
    } else if (isItemsMigrationError(items.reason)) {
      itemsState = 'migration_required'
      itemsError = items.reason instanceof ApiError ? items.reason.message : ITEMS_MIGRATION_STATE
    } else if (isAuthError(items.reason)) {
      itemsState = 'error'; itemsError = 'Sign in to load calendar notes/links/files/messages.'
    } else {
      itemsState = 'error'; itemsError = humanizeCalendarError(items.reason)
    }

    setState({
      loading: false, error, authRequired: false, google,
      days: buildMonthDays(year, month, all),
      itemsState, itemsError, items: itemRows,
    })
  }, [year, month])

  useEffect(() => {
    const ac = new AbortController()
    void load(ac.signal)
    return () => ac.abort()
  }, [load])

  return { ...state, reload: () => void load() }
}

// ---- real actions ------------------------------------------------------------

export interface DraftInput {
  title: string
  dateKey: string
  startTime: string // HH:MM
  durationMinutes: number
  description?: string
}

export async function createDraftEvent(input: DraftInput) {
  const start = new Date(`${input.dateKey}T${input.startTime}:00`)
  const end = new Date(start.getTime() + input.durationMinutes * 60000)
  return api.quamCalendar.draftEvent({
    title: input.title, description: input.description || null,
    start_at: start.toISOString(), end_at: end.toISOString(),
  })
}

export const approveDraft = (draftId: string) => api.quamCalendar.approveEvent(draftId)
export const commitDraft = (draftId: string) => api.quamCalendar.commitEvent(draftId)
export const createCallFromDraft = (draftId: string) => api.quamCalendar.createMeeting(draftId)

/** Task with a REAL due date — the tasks API accepts due_at on create. */
export async function createDueTask(title: string, dateKey: string): Promise<TeamTask> {
  const r = await apiRequest<{ ok: true; task: TeamTask }>('/api/square/team/tasks', {
    method: 'POST', body: { action: 'create', title, due_at: `${dateKey}T17:00:00.000Z` },
  })
  return r.task
}

export const setTaskStatus = (id: string, status: 'open' | 'in_progress' | 'blocked' | 'done') =>
  mutateTask('update_status', { id, status })

export async function checkAvailability(dateKey: string) {
  return api.quamCalendar.freebusy({
    time_min: `${dateKey}T00:00:00.000Z`, time_max: `${dateKey}T23:59:59.000Z`,
  })
}

export async function askQuamToPlan(request: string, dateKey: string) {
  return api.quamCalendar.plan({ request_text: `${request} (target day: ${dateKey})` })
}

// Workspace items (notes / links / messages) — real rows once 20260711000001 applies.
export const addCalendarItem = (payload: {
  anchor_kind: string; anchor_id: string; anchor_date?: string
  item_type: 'note' | 'link' | 'file' | 'message'
  title?: string; body?: string; url?: string
  file_bucket?: string; file_path?: string; file_name?: string
}) => api.quamCalendar.workspaceItems.create(payload)
export const updateCalendarItem = (id: string, patch: { title?: string; body?: string; url?: string }) =>
  api.quamCalendar.workspaceItems.update(id, patch)
export const removeCalendarItem = (id: string) => api.quamCalendar.workspaceItems.remove(id)

/** REAL file attach: signed upload URL on the private files bucket → PUT →
 *  record the ref as a calendar item. A missing bucket/storage error surfaces
 *  as STORAGE_PROVIDER_REQUIRED with the server's message. */
export async function attachCalendarFile(file: File, anchor: { anchor_kind: string; anchor_id: string; anchor_date?: string }) {
  const path = `calendar/${anchor.anchor_kind}/${anchor.anchor_id}/${Date.now()}-${file.name}`.replace(/[^a-zA-Z0-9/._-]/g, '_')
  let signed: { signedUrl: string; bucket: string; path: string }
  try {
    signed = await api.files.uploadUrl('files', path, file.type || 'application/octet-stream')
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'storage unavailable'
    throw new Error(`STORAGE_PROVIDER_REQUIRED — ${msg}`)
  }
  const put = await fetch(signed.signedUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file })
  if (!put.ok) throw new Error(`STORAGE_PROVIDER_REQUIRED — upload rejected (HTTP ${put.status})`)
  return addCalendarItem({ ...anchor, item_type: 'file', title: file.name, file_bucket: signed.bucket, file_path: signed.path, file_name: file.name })
}

export async function openCalendarFile(item: CalendarWorkspaceItem) {
  if (!item.file_bucket || !item.file_path) throw new Error('no file ref on this item')
  const r = await api.files.download(item.file_bucket, item.file_path)
  window.open(r.signedUrl, '_blank', 'noopener')
}

export async function deleteCalendarFile(item: CalendarWorkspaceItem) {
  if (item.file_bucket && item.file_path) {
    await api.files.delete(item.file_bucket, item.file_path).catch(() => { /* the item row is still removed; storage error surfaced by list */ })
  }
  await removeCalendarItem(item.id)
}

// ============================================================
// Priority Matrix (Eisenhower) — additive, read-only classification.
//
// A REPLACEABLE prototype policy: it derives importance + urgency from a task's
// REAL priority and due date only. It never mutates a record and never silently
// overrides an explicit user decision — future inputs (owner priority, project
// priority, explicit importance, policy, agent recommendation) plug into
// classifyEisenhower without changing the CalItem model or any mapper. Non-task
// kinds (events / calls / meetings / drafts / plans) carry no priority axis and
// are surfaced as time-anchored "appointments" rather than forced into a
// quadrant. The CalItem shape is unchanged; the quadrant is derived from raw.
// ============================================================

export type Quadrant = 'do_now' | 'schedule' | 'delegate' | 'reconsider'
export type MatrixBucket = Quadrant | 'appointments'

export interface QuadrantMeta {
  key: Quadrant
  label: string
  axis: string
  color: string
}

export const QUADRANT_META: Record<Quadrant, QuadrantMeta> = {
  do_now: { key: 'do_now', label: 'Do now', axis: 'Important · Urgent', color: '#e85d3a' },
  schedule: { key: 'schedule', label: 'Schedule', axis: 'Important · Not urgent', color: '#00a67d' },
  delegate: { key: 'delegate', label: 'Delegate', axis: 'Not important · Urgent', color: '#c4a24e' },
  reconsider: { key: 'reconsider', label: 'Reconsider', axis: 'Not important · Not urgent', color: '#5B9FD4' },
}

export const QUADRANT_ORDER: Quadrant[] = ['do_now', 'schedule', 'delegate', 'reconsider']

/** Tasks due within this many days (or already overdue) count as urgent. Policy input. */
export const URGENT_WINDOW_DAYS = 3

/** Policy id — bump when the rule changes so receipts/tests pin the version. */
export const CLASSIFICATION_POLICY = 'task_priority_due_v1'

export interface EisenhowerClassification {
  bucket: MatrixBucket
  important: boolean | null
  urgent: boolean | null
  reason: string
  policy: string
}

/**
 * Derive an Eisenhower quadrant from a task's real priority + due date. This is
 * the single replaceable policy seam. `now` is injectable so the rule is
 * deterministic under test.
 */
export function classifyEisenhower(item: CalItem, now: Date = new Date()): EisenhowerClassification {
  // Only tasks carry the two real axes (priority + due date). Everything else is
  // time-anchored and is honestly kept out of the quadrants.
  if (item.kind !== 'task') {
    return {
      bucket: 'appointments',
      important: null,
      urgent: null,
      reason: `${CAL_KIND_META[item.kind].label} is time-anchored, not priority-ranked`,
      policy: CLASSIFICATION_POLICY,
    }
  }
  const raw = item.raw as Partial<TeamTask>
  const priority: string = typeof raw.priority === 'string' ? raw.priority : 'normal'
  const important = priority === 'high' || priority === 'urgent'
  const dueMs = raw.due_at ? new Date(raw.due_at).getTime() : NaN
  let urgent = priority === 'urgent'
  if (Number.isFinite(dueMs)) {
    const days = (dueMs - now.getTime()) / 86400000
    if (days <= URGENT_WINDOW_DAYS) urgent = true
  }
  const bucket: Quadrant = important
    ? (urgent ? 'do_now' : 'schedule')
    : (urgent ? 'delegate' : 'reconsider')
  const dueNote = Number.isFinite(dueMs) ? `, due ${item.timeLabel || item.dateKey}` : ''
  return { bucket, important, urgent, reason: `priority=${priority}${dueNote}`, policy: CLASSIFICATION_POLICY }
}

export interface MatrixGroups {
  do_now: CalItem[]
  schedule: CalItem[]
  delegate: CalItem[]
  reconsider: CalItem[]
  appointments: CalItem[]
}

/** Group items into the four quadrants + a time-anchored appointments bucket. */
export function groupByQuadrant(items: CalItem[], now: Date = new Date()): MatrixGroups {
  const groups: MatrixGroups = { do_now: [], schedule: [], delegate: [], reconsider: [], appointments: [] }
  for (const item of items) groups[classifyEisenhower(item, now).bucket].push(item)
  return groups
}

// ============================================================
// Normalized read-model projection (packet §normalized_read_model).
//
// A READ-ONLY projection of a CalItem into the packet's normalized event shape.
// It is NOT a source of truth (source_of_truth = false): every write returns to
// the canonical owner via source_route. organization_id is resolvable only
// server-side (project_id → sq_projects.org_id) and is therefore left null in
// this frontend projection rather than faked.
// ============================================================

/** In this read projection every returned item is readable (it came back from
 *  the per-caller authorized load). Write authorization is resolved server-side
 *  by the canonical owner — the browser is never authoritative. */
export type PermissionState = 'readable'

export interface NormalizedCalendarEvent {
  source_type: CalKind
  source_record_id: string
  organization_id: string | null
  project_id: string | null
  title: string
  start_at: string | null
  end_at: string | null
  status: string | null
  importance: 'high' | 'normal' | null
  urgency: 'urgent' | 'normal' | null
  permission_state: PermissionState
  source_route: string
}

/** Canonical owner route each source_type writes back to (this projection owns nothing). */
export const NORMALIZED_SOURCE_ROUTE: Record<CalKind, string> = {
  google_event: '/api/square/command-center/quam-calendar/events',
  draft: '/api/square/command-center/quam-calendar/events',
  plan: '/api/square/command-center/quam-calendar/events',
  call: '/api/square/daily/meetings',
  daily_meeting: '/api/square/command-center/daily-meetings',
  task: '/api/square/team/tasks',
}

/** Read a non-empty string field off an unknown record (else null). */
function pickStr(raw: Record<string, unknown>, key: string): string | null {
  const v = raw[key]
  return typeof v === 'string' && v ? v : null
}

function normalizedStart(item: CalItem): string | null {
  const raw = item.raw
  switch (item.kind) {
    case 'google_event': {
      const start = raw.start as Record<string, unknown> | undefined
      return (start && (pickStr(start, 'dateTime') || pickStr(start, 'date'))) || null
    }
    case 'draft': return pickStr(raw, 'start_at')
    case 'plan': return pickStr(raw, 'created_at')
    case 'call': return pickStr(raw, 'starts_at') || pickStr(raw, 'created_at')
    case 'daily_meeting': return pickStr(raw, 'meeting_date')
    case 'task': return pickStr(raw, 'due_at')
    default: return null
  }
}

function normalizedEnd(item: CalItem): string | null {
  const raw = item.raw
  switch (item.kind) {
    case 'google_event': {
      const end = raw.end as Record<string, unknown> | undefined
      return (end && (pickStr(end, 'dateTime') || pickStr(end, 'date'))) || null
    }
    case 'draft': return pickStr(raw, 'end_at')
    case 'call': return pickStr(raw, 'expires_at')
    // plan / daily_meeting / task have no real end instant — honestly null.
    default: return null
  }
}

/**
 * Project a CalItem into the packet's normalized read model. Read-only; the
 * result carries source_route back to the canonical owner for any write.
 */
export function toNormalizedEvent(item: CalItem): NormalizedCalendarEvent {
  const raw = item.raw
  const cls = classifyEisenhower(item)
  return {
    source_type: item.kind,
    source_record_id: item.id,
    organization_id: null, // resolvable only via project_id → sq_projects (server-side)
    project_id: typeof raw.project_id === 'string' ? raw.project_id : null,
    title: item.title,
    start_at: normalizedStart(item),
    end_at: normalizedEnd(item),
    status: item.status ?? null,
    importance: cls.important == null ? null : (cls.important ? 'high' : 'normal'),
    urgency: cls.urgent == null ? null : (cls.urgent ? 'urgent' : 'normal'),
    permission_state: 'readable',
    source_route: NORMALIZED_SOURCE_ROUTE[item.kind],
  }
}
